import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Flame, Users, MapPin, Calendar, Star, ChevronRight } from 'lucide-react';
import SiteNav from '../components/SiteNav';
import FadeImage from '../components/FadeImage';
import { contentApi } from '../lib/api';

const DEFAULT_IMAGES = {
  hero_image_url: 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=600&q=80',
  about_image_url: 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=600&q=80',
};

export default function HomePage() {
  const [images, setImages] = useState(null);

  useEffect(() => {
    contentApi.getHomepageContent()
      .then(r => setImages(r.data && Object.keys(r.data).length ? { ...DEFAULT_IMAGES, ...r.data } : DEFAULT_IMAGES))
      .catch(() => setImages(DEFAULT_IMAGES));
  }, []);

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <SiteNav />

      <Hero heroImage={images?.hero_image_url} heroLqip={images?.hero_image_lqip} loading={!images} />
      <Offerings />
      <AboutMovement aboutImage={images?.about_image_url} aboutLqip={images?.about_image_lqip} loading={!images} />
      <WhyKalari />
      <HomeCTA />
      <HomeFooter />
    </div>
  );
}

function Hero({ heroImage, heroLqip, loading }) {
  return (
    <section className="relative min-h-screen bg-[#1C1917] flex items-center overflow-hidden pt-16">
      {/* Subtle texture overlay */}
      <div className="absolute inset-0 opacity-20"
        style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, #9A3B26 0%, transparent 50%), radial-gradient(circle at 80% 20%, #DDA15E 0%, transparent 40%)' }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-24 grid lg:grid-cols-2 gap-16 items-center">
        <div className="space-y-8">
          <a href="/kalaripayattu" className="inline-flex items-center gap-2 border border-[#DDA15E]/40 text-[#DDA15E] rounded-full px-4 py-1.5 text-xs font-semibold tracking-widest uppercase hover:bg-[#DDA15E]/10 transition-colors">
            <Flame className="h-3.5 w-3.5" />
            Kalaripayattu
          </a>

          <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-[1.05]">
            Ancient Wisdom.<br />
            <span className="text-[#DDA15E]">Modern Practice.</span>
          </h1>

          <p className="text-lg text-white/65 max-w-lg leading-relaxed">
            The Kalari Movement is dedicated to bringing the world's oldest martial art — Kalaripayattu — to practitioners everywhere. Train your body, sharpen your mind, and connect with a living tradition.
          </p>

          <div className="flex flex-wrap gap-4">
            <Link
              to="/programme"
              className="inline-flex items-center gap-2 bg-[#9A3B26] hover:bg-[#7A2E1E] text-white font-medium px-7 py-3.5 rounded-full transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              Explore the 21 Days Kalari Sadhana Programme
              <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 border border-white/25 hover:border-white/60 text-white font-medium px-7 py-3.5 rounded-full transition-all duration-300"
            >
              Get in Touch
            </Link>
          </div>

          <div className="flex items-center gap-6 pt-2">
            <Stat value="2500+" label="Practitioners" />
            <div className="w-px h-8 bg-white/15" />
            <Stat value="21 Days" label="Online Programme" />
            <div className="w-px h-8 bg-white/15" />
            <Stat value="3000+" label="Years of History" />
          </div>
        </div>

        <div className="hidden lg:block">
          <div className="relative">
            <div className="aspect-[4/5] rounded-2xl overflow-hidden bg-[#2C2420]">
              {loading
                ? <div className="w-full h-full animate-pulse bg-[#3C3430]" />
                : <FadeImage src={heroImage} alt="Kalaripayattu practice" className="w-full h-full object-cover opacity-80" skeletonClass="bg-[#3C3430]" lqip={heroLqip} />
              }
            </div>
            <div className="absolute -bottom-6 -left-6 bg-[#9A3B26] rounded-2xl px-6 py-4 shadow-2xl">
              <p className="text-white/70 text-xs font-medium uppercase tracking-widest mb-1">Next Batch Starts</p>
              <p className="text-white font-heading font-bold text-lg">October 1, 2026</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Stat({ value, label }) {
  return (
    <div>
      <p className="text-white font-bold text-lg leading-tight">{value}</p>
      <p className="text-white/50 text-xs">{label}</p>
    </div>
  );
}

function Offerings() {
  const offerings = [
    {
      icon: <Calendar className="h-6 w-6" />,
      badge: 'Now Enrolling',
      badgeColor: 'bg-[#9A3B26] text-white',
      title: '21-Day Online Programme',
      description: 'A structured, live-led programme that takes you from foundations to flowing sequences in 21 days. Daily Zoom sessions, recorded replays, and personal guidance.',
      cta: 'View Programme',
      to: '/programme',
      active: true,
    },
    {
      icon: <MapPin className="h-6 w-6" />,
      badge: 'Coming Soon',
      badgeColor: 'bg-[#F4EFE6] text-[#78716C]',
      title: 'In-Person Classes',
      description: 'Train with our instructors at our physical centre. Small batches, personalised attention, and the full Kalari experience — weapons, conditioning, and healing.',
      cta: 'Join Waitlist',
      to: '/contact',
      active: false,
    },
    {
      icon: <Star className="h-6 w-6" />,
      badge: 'Coming Soon',
      badgeColor: 'bg-[#F4EFE6] text-[#78716C]',
      title: 'Workshops & Retreats',
      description: 'Immersive weekend workshops and multi-day retreats for deeper practice. Ideal for existing practitioners and serious students looking to accelerate their journey.',
      cta: 'Join Waitlist',
      to: '/contact',
      active: false,
    },
  ];

  return (
    <section className="py-24 px-6 md:px-12 lg:px-24 bg-[#FDFBF7]">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <p className="text-[#9A3B26] font-semibold text-sm uppercase tracking-widest mb-3">What We Offer</p>
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-[#292524]">
            Many Paths, One Art
          </h2>
          <p className="mt-4 text-[#78716C] max-w-xl mx-auto">
            Whether you're beginning online or training in person, every path leads to the same place — a deeper connection with yourself.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {offerings.map((o) => (
            <div key={o.title} className={`rounded-2xl p-8 flex flex-col ${o.active ? 'bg-[#1C1917] text-white' : 'bg-white border border-[#E7E5E4]'}`}>
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-6 ${o.active ? 'bg-[#9A3B26]' : 'bg-[#F4EFE6]'}`}>
                <span className={o.active ? 'text-white' : 'text-[#9A3B26]'}>{o.icon}</span>
              </div>

              <span className={`inline-block text-xs font-semibold px-3 py-1 rounded-full mb-4 self-start ${o.badgeColor}`}>
                {o.badge}
              </span>

              <h3 className={`font-heading text-xl font-bold mb-3 ${o.active ? 'text-white' : 'text-[#292524]'}`}>
                {o.title}
              </h3>
              <p className={`text-sm leading-relaxed flex-1 ${o.active ? 'text-white/65' : 'text-[#78716C]'}`}>
                {o.description}
              </p>

              <Link
                to={o.to}
                className={`mt-8 inline-flex items-center gap-2 text-sm font-semibold transition-colors ${o.active ? 'text-[#DDA15E] hover:text-white' : 'text-[#9A3B26] hover:text-[#7A2E1E]'}`}
              >
                {o.cta} <ChevronRight className="h-4 w-4" />
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function AboutMovement({ aboutImage, aboutLqip, loading }) {
  return (
    <section className="py-24 px-6 md:px-12 lg:px-24 bg-[#F4EFE6]">
      <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
        <div>
          <div className="aspect-[4/3] rounded-2xl overflow-hidden bg-[#E7DDD5]">
            {loading
              ? <div className="w-full h-full animate-pulse bg-[#DDD5C8]" />
              : <FadeImage src={aboutImage} alt="Kalaripayattu" className="w-full h-full object-cover" lqip={aboutLqip} />
            }
          </div>
        </div>

        <div className="space-y-6">
          <p className="text-[#9A3B26] font-semibold text-sm uppercase tracking-widest">The Movement</p>
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-[#292524] leading-tight">
            More Than a Martial Art
          </h2>
          <p className="text-[#78716C] leading-relaxed">
            Kalaripayattu is over 3,000 years old — originating in the forests of Kerala, India. It is considered the mother of all martial arts, weaving together combat, healing, and spiritual practice into one unified system.
          </p>
          <p className="text-[#78716C] leading-relaxed">
            The Kalari Movement was born from a conviction that this ancient knowledge should not remain locked behind geography or language. We bring authentic, lineage-based Kalaripayattu to practitioners worldwide — online and in-person — taught by masters who have trained their entire lives in the tradition.
          </p>

          <div className="grid grid-cols-2 gap-4 pt-4">
            {[['3000+ Years', 'Living tradition'], ['Kerala, India', 'Place of origin'], ['Online + In-person', 'How we teach'], ['All levels', 'Who can join']].map(([v, l]) => (
              <div key={v} className="bg-white rounded-xl p-4">
                <p className="font-heading font-bold text-[#9A3B26] text-lg">{v}</p>
                <p className="text-[#78716C] text-sm">{l}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function WhyKalari() {
  const reasons = [
    { title: 'Full-Body Strength', body: 'Kalari develops explosive power, flexibility, and functional movement through sequences that challenge every muscle group.' },
    { title: 'Mental Clarity', body: 'The intense focus required during practice quiets the mind and builds concentration that carries into everyday life.' },
    { title: 'Cultural Rootedness', body: 'You are not just learning movements — you are connecting to a lineage of warriors, healers, and philosophers.' },
    { title: 'Healing System', body: "Kalari includes Kalari Chikitsa — a traditional healing system using massage and herbal treatment. It's martial art and medicine together." },
  ];

  return (
    <section className="py-24 px-6 md:px-12 lg:px-24 bg-[#FDFBF7]">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <p className="text-[#9A3B26] font-semibold text-sm uppercase tracking-widest mb-3">Why Kalaripayattu</p>
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-[#292524]">
            Train the Body. Free the Mind.
          </h2>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {reasons.map((r, i) => (
            <div key={r.title} className="space-y-4">
              <div className="w-10 h-10 rounded-full bg-[#F4EFE6] flex items-center justify-center font-heading font-bold text-[#9A3B26]">
                {i + 1}
              </div>
              <h3 className="font-heading text-lg font-bold text-[#292524]">{r.title}</h3>
              <p className="text-[#78716C] text-sm leading-relaxed">{r.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HomeCTA() {
  return (
    <section className="py-24 px-6 md:px-12 bg-[#1C1917]">
      <div className="max-w-3xl mx-auto text-center space-y-6">
        <h2 className="font-heading text-4xl md:text-5xl font-bold text-white">
          Ready to Begin?
        </h2>
        <p className="text-white/60 text-lg">
          The next 21-day batch starts July 18, 2026. Limited spots available.
        </p>
        <div className="flex flex-wrap gap-4 justify-center">
          <Link to="/programme" className="inline-flex items-center gap-2 bg-[#9A3B26] hover:bg-[#7A2E1E] text-white font-medium px-8 py-4 rounded-full transition-all duration-300 shadow-lg">
            View the Programme <ArrowRight className="h-4 w-4" />
          </Link>
          <Link to="/contact" className="inline-flex items-center gap-2 border border-white/25 hover:border-white/60 text-white font-medium px-8 py-4 rounded-full transition-all duration-300">
            Ask a Question
          </Link>
        </div>
      </div>
    </section>
  );
}

function HomeFooter() {
  return (
    <footer className="bg-[#141210] text-white/50 py-10 px-6 md:px-12">
      <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4 text-sm">
        <span className="font-heading font-bold text-white text-base">The Kalari Movement</span>
        <div className="flex gap-6">
          <Link to="/" className="hover:text-white transition-colors">Home</Link>
          <Link to="/about" className="hover:text-white transition-colors">About Us</Link>
          <Link to="/programme" className="hover:text-white transition-colors">Programme</Link>
          <Link to="/contact" className="hover:text-white transition-colors">Contact</Link>
        </div>
        <span>© {new Date().getFullYear()} The Kalari Movement. All rights reserved.</span>
      </div>
    </footer>
  );
}
